import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { Box, Card, Tabs, Tab, Grid, Typography, MenuItem, Select, CircularProgress, TextField, FormControl, InputLabel, Button, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Table, Chip } from "@mui/material";
import TablePagination from '@mui/material/TablePagination';
import { Doughnut } from 'react-chartjs-2';
import GridOnIcon from '@mui/icons-material/GridOn';
import PeopleIcon from '@mui/icons-material/People';
import CircleIcon from '@mui/icons-material/Circle';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { getLocationList, getLocation } from '../../../helpers/commonHelper';
import { makeApiRequest } from '../../../utils/api';
import { trimSeconds, formatTimeToHoursMinutes, formatDate, generateDateHeaders, formatMonth } from '../../../helpers/timeHelper';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
ChartJS.register(ArcElement, Tooltip, Legend);

const getStatusBadge = (status) => {
    let chipProps = {};

    switch (status) {
        case "Present":
            chipProps = { sx: { width: "100px", height: "28px", backgroundColor: "#CBFAF0", color: "#298880", border: "1px solid #98EEDB", borderRadius: "4px", padding: "4px 10px" } };
            break;
        case "Absent":
            chipProps = { sx: { width: "100px", height: "28px", backgroundColor: "#FDE6EB", color: "#BA3068", border: "1px solid #FCD6DE", borderRadius: "4px", padding: "4px 10px" } };
            break;
        case "Pending":
            chipProps = {
                sx: {
                    backgroundColor: "#FFFFA3",
                    color: "#FF8000",
                    border: "1px solid #FF9900",
                    borderRadius: "4px",
                    padding: "4px 10px",
                    width: "100px",
                }
            };
            break;
        default:
            chipProps = {
                sx: {
                    height: "28px",
                    backgroundColor: "#F5F5F5",
                    color: "#595959",
                    border: "1px solid #D9D9D9",
                    borderRadius: "4px",
                    padding: "4px 10px",
                    width: "100px",
                }
            };
    }

    return <Chip label={status} size="small" {...chipProps} />;
};

const BranchAdminAttendance = () => {
    const todayDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    const [month, day, year] = todayDate.split('/');
    const formattedDate = `${year}-${month}-${day}`;
    var today_date = formattedDate;

    const [location, setLocation] = useState("All Locations");
    const [staff, setStaff] = useState("");
    const [error, setError] = useState(null);
    const [value, setValue] = useState('today');
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(today_date);
    const [endDate, setEndDate] = useState(today_date);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [attendanceData, setAttendanceData] = useState([]);
    const [exportData, setExportData] = useState([]);
    const [donutData, setDonutData] = useState({
        labels: ['Present', 'Absent'],
        datasets: [{
            label: 'Attendance',
            data: [0, 0],
            backgroundColor: ['green', '#D32F2F'],
            borderWidth: 1,
        }],
    });
    const [locations, setLocations] = useState([]);
    const [dashboardData, setDashboardData] = useState({});
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });
    const [searchText, setSearchText] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const fetchAttendanceData = async () => {
        setLoading(true);
        setError(null);
        setHasSubmitted(true);

        try {
            const response = await makeApiRequest('post', '/api/attendance/report', {
                start_date: startDate,
                end_date: endDate,
                location: location,
                name: staff,
                control: 4,
                token: localStorage.getItem('authToken'),
                page: page + 1,
                per_page: rowsPerPage,
            });

            if (response.status === 'success') {
                setAttendanceData([...response.data.list]);
                setDashboardData({
                    total: response.data.total_count,
                    present: response.data.total_present,
                    absent: response.data.total_absent,
                });
                setDonutData(prevData => ({
                    ...prevData,
                    datasets: [{
                        ...prevData.datasets[0],
                        data: [response.data.total_present || 0, response.data.total_absent || 0]
                    }]
                }));
            }
        } catch (err) {
            console.error('API Error:', err);
            setError(err.response?.data?.message || 'Error fetching attendance data');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllAttendanceForExport = async () => {
        try {
            const response = await makeApiRequest('post', '/api/attendance/export', {
                start_date: startDate,
                end_date: endDate,
                location: location,
                name: staff,
                control: 4,
                token: localStorage.getItem('authToken'),
            });

            if (response.status === 'success') {
                setExportData([...response.data.list]);
                return response.data.list;
            } else {
                throw new Error('Failed to fetch export data');
            }
        } catch (err) {
            console.error('Export API Error:', err);
            toast.error(err.response?.data?.message || 'Error fetching export data');
            return [];
        }
    };

    useEffect(() => {
        fetchAttendanceData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const getStaffListForLocation = async (loc) => {
        try {
            const response = await makeApiRequest('post', '/api/team/list', {
                token: localStorage.getItem('authToken'),
                data: {
                    control: 4,
                    location: loc === 'All Locations' ? '' : loc,
                    per_page: 500,
                }
            });

            if (response.status === 'success') {
                setStaffList(response.data.list || []);
            } else {
                throw new Error('Failed to fetch branch admin list');
            }
        } catch (err) {
            console.error('Error fetching branch admin list:', err);
            setError(err.response?.data?.message || 'Error fetching branch admin list');
        }
    };

    useEffect(() => {
        listLocation();
        if (value === 'today') {
            fetchAttendanceData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, location, staff]);

    useEffect(() => {
        getStaffListForLocation(location);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const listLocation = () => {
        const locationList = getLocationList();
        setLocations(locationList);
    };

    const handleExport = async () => {
        const data = await fetchAllAttendanceForExport();

        if (!data || data.length === 0) {
            toast.warn("No data available to export.");
            return;
        }

        if (value === "today") {
            const doc = new jsPDF();
            const title = "Branch Admin Attendance Report (Today)";
            const headers = [["Name", "Date", "Punch In", "Punch Out", "Delay Time", "Working Hours", "Status"]];
            const exportData = data.map((item) => [
                item.name,
                item.date,
                item.intime || "-",
                item.outtime || "-",
                formatTimeToHoursMinutes(item.delay) || "-",
                formatTimeToHoursMinutes(item.workinghour) || "-",
                item.attendance_status || "-",
            ]);
            doc.setFontSize(16);
            doc.text(title, 14, 15);
            doc.autoTable({
                startY: 20,
                head: headers,
                body: exportData,
            });
            doc.save("today_branch_admin_attendance_report.pdf");
        } else if (value === "historical") {
            const doc = new jsPDF();
            const title = "Branch Admin Attendance Report (Historical)";
            const rawHeaders = generateDateHeaders(startDate, endDate);
            const headers = ["Name", ...rawHeaders.map(date => formatDate(date))];
            const exportData = data.map((item) => [
                item.name,
                ...generateDateHeaders(startDate, endDate).map((date) =>
                    item.month[formatMonth(date)] || "-"
                ),
            ]);
            doc.setFontSize(16);
            doc.text(title, 14, 15);
            doc.autoTable({
                startY: 20,
                head: [headers],
                body: exportData,
            });
            doc.save("historical_branch_admin_attendance_report.pdf");
        } else {
            toast.warn("Invalid tab selection.");
        }
    };

    const handleExportExcel = async () => {
        try {
            const data = await fetchAllAttendanceForExport();

            if (!data || data.length === 0) {
                toast.warn("No data available to export.");
                return;
            }

            let worksheet;
            let fileName;

            if (value === "today") {
                fileName = "today_branch_admin_attendance_report.xlsx";
                const exportData = data.map((item) => ({
                    Name: item.name,
                    Date: item.date,
                    "Punch In": item.intime || "-",
                    "Punch Out": item.outtime || "-",
                    "Delay Time": formatTimeToHoursMinutes(item.delay) || "-",
                    "Working Hours": formatTimeToHoursMinutes(item.workinghour) || "-",
                    Status: item.attendance_status || "-",
                }));
                worksheet = XLSX.utils.json_to_sheet(exportData);
            } else if (value === "historical") {
                fileName = "historical_branch_admin_attendance_report.xlsx";
                const rawHeaders = generateDateHeaders(startDate, endDate);
                const headers = ["Name", ...rawHeaders.map(date => formatDate(date))];
                const exportData = data.map((item) => {
                    let rowData = { Name: item.name };
                    rawHeaders.forEach(date => {
                        rowData[formatDate(date)] = item.month?.[formatMonth(date)] || "-";
                    });
                    return rowData;
                });
                worksheet = XLSX.utils.json_to_sheet(exportData);
                XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });
            } else {
                toast.warn("Invalid tab selection.");
                return;
            }

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Report");
            XLSX.writeFile(workbook, fileName);

        } catch (error) {
            console.error("Error exporting Excel:", error);
            toast.error("Failed to export attendance data.");
        }
    };

    const getPaginatedData = () => {
        if (!Array.isArray(attendanceData)) {
            return [];
        }
        return attendanceData;
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = (data) => {
        if (!sortConfig.key || !Array.isArray(data)) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key] || '';
            const bValue = b[sortConfig.key] || '';

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getFilteredData = (data) => {
        if (!searchText) return data;

        return data.filter(item =>
            item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.userid?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.attendance_status?.toLowerCase().includes(searchText.toLowerCase())
        );
    };

    if (loading) return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
        >
            <CircularProgress
                size={40}
                thickness={4}
                sx={{
                    color: '#481E82',
                }}
            />
        </Box>
    );

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
            },
        },
        cutout: '70%',
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
        setHasSubmitted(false);

        if (newValue === 'today') {
            fetchAttendanceData();
            setStartDate(today_date);
            setEndDate(today_date);
        } else {
            setStartDate("");
            setEndDate("");
        }
        setLocation("All Locations");
        setStaff("");
        setValue(newValue);
        setAttendanceData([]);
        setExportData([]);
        setDashboardData({
            total: 0,
            present: 0,
            absent: 0
        });
    };

    const handleLocationChange = async (event) => {
        setLocation(event.target.value);
        getStaffListForLocation(event.target.value);
    };

    const handleStaffChange = (event) => {
        setStaff(event.target.value);
        setPage(0);
        setAttendanceData([]);
        setExportData([]);
        setDashboardData({
            total: 0,
            present: 0,
            absent: 0
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchAttendanceData();
    };

    const handleSubmit = async () => {
        try {
            if (value === 'historical') {
                if (startDate === '' || endDate === '') {
                    toast.warn("Please choose start date and end date");
                    return;
                }
            }
            setLoading(true);
            setError(null);
            setPage(0);
            setHasSubmitted(true);

            const response = await makeApiRequest('post', '/api/attendance/report', {
                token: localStorage.getItem('authToken'),
                start_date: startDate,
                end_date: endDate,
                location: location,
                name: staff,
                control: 4,
                page: page + 1,
                per_page: rowsPerPage,
            });

            if (response.status === 'success') {
                setAttendanceData([...response.data.list]);
                setDashboardData({
                    total: response.data.total_count,
                    present: response.data.total_present,
                    absent: response.data.total_absent
                });
                setDonutData(prevData => ({
                    ...prevData,
                    datasets: [{
                        ...prevData.datasets[0],
                        data: [response.data.total_present || 0, response.data.total_absent || 0]
                    }]
                }));
            } else {
                setAttendanceData([]);
                setExportData([]);
                setDashboardData({
                    total: 0,
                    present: 0,
                    absent: 0
                });
                throw new Error('Invalid attendance data received');
            }
        } catch (err) {
            console.error('Error fetching attendance data:', err);
            setError(err.response?.data?.message || 'Error fetching attendance data');
            setAttendanceData([]);
            setExportData([]);
            setDashboardData({
                total: 0,
                present: 0,
                absent: 0
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Box sx={{ minHeight: '100vh', backgroundColor: '#ECEDFF', width: "100%", maxHeight: 'auto' }}>
                <ToastContainer
                    position="top-center"
                    autoClose={2000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick={false}
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover={false}
                    theme="light"
                />
                <Grid container spacing={2}>
                    <Grid item xs={12} style={{ height: "80px", display: "flex", alignItems: "center" }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontFamily: "Roboto",
                                fontSize: { xs: "24px", sm: "34px" },
                                fontStyle: "normal",
                                fontWeight: "500",
                                lineHeight: "160%",
                                letterSpacing: "0.15px",
                                color: "#481E82",
                                paddingLeft: '15px',
                            }}
                        >
                            Branch Admin Attendance Reports
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Card sx={{ display: "flex", justifyContent: { xs: 'flex-start', sm: "flex-start" }, alignItems: "center", height: "100%", width: "97.5%" }}>
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="centered tabs example"
                                    centered
                                >
                                    <Tab
                                        label="Today"
                                        value='today'
                                        sx={{ fontWeight: value === 'today' ? 'bold' : 'normal' }}
                                    />
                                    <Tab
                                        label="Historical"
                                        value='historical'
                                        sx={{ fontWeight: value === 'historical' ? 'bold' : 'normal' }}
                                    />
                                </Tabs>
                            </Card>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ display: "flex", backgroundColor: "none", justifyContent: { xs: 'flex-start', sm: "flex-start" }, alignItems: "center", width: "97.5%" }}>
                                {value === 'today' && (
                                    <Grid container spacing={2} sx={{ paddingLeft: 0, paddingRight: 0 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Card sx={{ padding: 2, height: { xs: "auto", sm: '80%', display: "flex", flexDirection: "column", justifyContent: 'space-evenly' } }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                            <InputLabel id="dropdown-label">Select Location</InputLabel>
                                                            <Select
                                                                labelId="dropdown-label"
                                                                id="dropdown"
                                                                value={location}
                                                                onChange={handleLocationChange}
                                                                label="Select Location"
                                                            >
                                                                <MenuItem value="All Locations">All Locations</MenuItem>
                                                                {locations && locations.map((loc) => (
                                                                    <MenuItem key={loc.id} value={loc.primary_location}>
                                                                        {loc.primary_location}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                            <InputLabel id="dropdown-label">Select Branch Admin</InputLabel>
                                                            <Select
                                                                labelId="dropdown-label"
                                                                id="dropdown"
                                                                value={staff}
                                                                onChange={handleStaffChange}
                                                                label="Select Branch Admin"
                                                            >
                                                                <MenuItem value="">All Branch Admins</MenuItem>
                                                                {staffList.map((staffMember, index) => (
                                                                    <MenuItem key={index} value={staffMember.userid}>
                                                                        {staffMember.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                                <Box sx={{ display: "flex", justifyContent: { xs: 'center', sm: "flex-end" }, marginTop: { xs: "10px" } }}>
                                                    <Button variant="contained" onClick={handleSubmit}>
                                                        Submit
                                                    </Button>
                                                </Box>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6}>
                                            <Card sx={{ padding: 2, height: { xs: "auto", sm: '80%' }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                                                <Typography variant="h6" gutterBottom>
                                                    <Box sx={{ height: "100%", width: "100%", backgroundColor: "white", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "5px" }}>
                                                        <div style={{ width: 132, height: 132, position: "relative" }}>
                                                            <Doughnut data={donutData} options={options} />
                                                            <Box>
                                                                <Typography style={{ position: "absolute", bottom: "75px", left: "53px" }} sx={{
                                                                    color: 'rgba(188, 184, 177, 0.87)',
                                                                    fontFamily: 'Roboto, sans-serif',
                                                                    fontSize: '11.127px',
                                                                    fontStyle: 'normal',
                                                                    fontWeight: 400,
                                                                    lineHeight: '16.69px',
                                                                    letterSpacing: '0.104px',
                                                                }}> Total </Typography>
                                                                <Typography style={{
                                                                    position: "absolute", bottom: "45px",
                                                                    left: dashboardData.total < 10 ? "57px" :
                                                                        dashboardData.total < 100 ? "50px" :
                                                                        dashboardData.total < 1000 ? "43px" : "35px",
                                                                }} sx={{
                                                                    color: '#481E82',
                                                                    fontFamily: 'Roboto, sans-serif',
                                                                    fontSize: '28px',
                                                                    fontStyle: 'normal',
                                                                    fontWeight: 900,
                                                                    lineHeight: '123.5%',
                                                                    letterSpacing: '0.174px',
                                                                }}> {dashboardData.total}</Typography>
                                                            </Box>
                                                        </div>
                                                    </Box>
                                                </Typography>
                                                <Box sx={{ height: "100%", width: { xs: "100%", sm: '55%' }, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                    <Box sx={{ height: "80%", width: "100%" }}>
                                                        <Box sx={{
                                                            height: "33%", border: "2px solid violet", borderRadius: "5px", display: "flex", alignItems: 'center', justifyContent: 'center', transition: "all 0.3s ease-in-out", padding: '1px',
                                                            '&:hover': { backgroundColor: "#f1f1f1", transform: "translateX(5px)" }
                                                        }}>
                                                            <Grid container spacing={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <Grid item xs={5}>
                                                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                                                        <PeopleIcon />
                                                                        <Box sx={{ color: "#666666", marginLeft: 3 }}>Total</Box>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={5}>
                                                                    <Box sx={{ textAlign: "right" }}>
                                                                        <Typography variant="h5" sx={{ fontSize: "28px", fontWeight: "650", color: "blue" }}>{dashboardData.total}</Typography>
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                        <Box sx={{ height: "33%", display: "flex", alignItems: 'center', justifyContent: 'center', transition: "all 0.3s ease-in-out" }}>
                                                            <Grid container spacing={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <Grid item xs={5}>
                                                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                                                        <CircleIcon sx={{ color: "green", transition: "all 0.3s ease-in-out", '&:hover': { transform: "scale(1.2)" } }} />
                                                                        <Box sx={{ color: "#666666", marginLeft: 3 }}>Present</Box>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={5}>
                                                                    <Box sx={{ textAlign: "right" }}>
                                                                        <Typography variant="h5" sx={{ fontSize: "28px", fontWeight: "650", color: "blue" }}>{dashboardData.present}</Typography>
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                        <Box sx={{ height: "34%", display: "flex", alignItems: 'center', justifyContent: 'center', transition: "all 0.3s ease-in-out" }}>
                                                            <Grid container spacing={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <Grid item xs={5}>
                                                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                                                        <CircleIcon sx={{ color: "#D32F2F", transition: "all 0.3s ease-in-out", '&:hover': { transform: "scale(1.2)" } }} />
                                                                        <Box sx={{ color: "#666666", marginLeft: 3 }}>Absent</Box>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={5}>
                                                                    <Box sx={{ textAlign: "right" }}>
                                                                        <Typography variant="h5" sx={{ fontSize: "28px", fontWeight: "650", color: "blue" }}>{dashboardData.absent}</Typography>
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                )}
                                {value === 'historical' && (
                                    <Grid container spacing={0} sx={{ marginTop: "0px", paddingLeft: 0, paddingRight: 0 }}>
                                        <Grid item xs={12}>
                                            <Card sx={{ px: 2, py: 3, height: "auto" }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            label="Start Date"
                                                            type="date"
                                                            variant="outlined"
                                                            InputLabelProps={{ shrink: true }}
                                                            size='small'
                                                            sx={{
                                                                width: "100%",
                                                                "& input": { color: startDate ? "#000" : "#a2a2a2" },
                                                                "& input::-webkit-datetime-edit-text": { color: "#a2a2a2" },
                                                                "& input::-webkit-datetime-edit-year-field, & input::-webkit-datetime-edit-month-field, & input::-webkit-datetime-edit-day-field": {
                                                                    color: startDate ? "#000" : "#a2a2a2"
                                                                }
                                                            }}
                                                            value={startDate}
                                                            onChange={(event) => setStartDate(event.target.value)}
                                                            onFocus={(e) => e.target.showPicker()}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <TextField
                                                            label="End Date"
                                                            type="date"
                                                            variant="outlined"
                                                            InputLabelProps={{ shrink: true }}
                                                            size='small'
                                                            sx={{
                                                                width: "100%",
                                                                "& input": { color: endDate ? "#000" : "#a2a2a2" },
                                                                "& input::-webkit-datetime-edit-text": { color: "#a2a2a2" },
                                                                "& input::-webkit-datetime-edit-year-field, & input::-webkit-datetime-edit-month-field, & input::-webkit-datetime-edit-day-field": {
                                                                    color: endDate ? "#000" : "#a2a2a2"
                                                                }
                                                            }}
                                                            value={endDate}
                                                            onChange={(event) => setEndDate(event.target.value)}
                                                            onFocus={(e) => e.target.showPicker()}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                            <InputLabel id="dropdown-label">Select Location</InputLabel>
                                                            <Select
                                                                labelId="dropdown-label"
                                                                id="dropdown"
                                                                value={location}
                                                                onChange={handleLocationChange}
                                                                label="Select Location"
                                                            >
                                                                <MenuItem value="All Locations">All Locations</MenuItem>
                                                                {locations && locations.map((loc) => (
                                                                    <MenuItem key={loc.id} value={loc.primary_location}>
                                                                        {loc.primary_location}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                            <InputLabel id="dropdown-label">Select Branch Admin</InputLabel>
                                                            <Select
                                                                labelId="dropdown-label"
                                                                id="dropdown"
                                                                value={staff}
                                                                onChange={handleStaffChange}
                                                                label="Select Branch Admin"
                                                            >
                                                                <MenuItem value="">All Branch Admins</MenuItem>
                                                                {staffList.map((staffMember, index) => (
                                                                    <MenuItem key={index} value={staffMember.userid}>
                                                                        {staffMember.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                                <Box sx={{ display: "flex", justifyContent: { xs: 'center', sm: "flex-end" }, marginTop: { xs: "10px" } }}>
                                                    <Button variant="contained" onClick={handleSubmit}>
                                                        Submit
                                                    </Button>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ display: "flex", backgroundColor: "none", justifyContent: { xs: 'flex-start', sm: "flex-start" }, alignItems: "center", width: "100%" }}>
                                <Grid container style={{ display: "flex", justifyContent: "center", borderRadius: "0px" }}>
                                    <Grid item xs={11.7}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            background: "white",
                                            mt: 0,
                                            px: 2,
                                            py: 3
                                        }}>
                                            <Grid container spacing={2} sx={{ justifyContent: "space-between" }}>
                                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                                    <TextField
                                                        placeholder="Search by name or ID"
                                                        variant="outlined"
                                                        size="small"
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                        sx={{
                                                            width: "100%",
                                                            '& .MuiOutlinedInput-root': { height: '40px' },
                                                            backgroundColor: 'white'
                                                        }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={7} md={6}>
                                                    <Grid container spacing={0} sx={{ justifyContent: "flex-end" }}>
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleExport}
                                                            startIcon={<GridOnIcon />}
                                                            disabled={!attendanceData.length}
                                                            sx={{
                                                                backgroundColor: "orange",
                                                                width: { xs: "100%", sm: "fit-content" },
                                                                marginRight: { xs: 0, sm: 2 }
                                                            }}>
                                                            Download as PDF
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleExportExcel}
                                                            startIcon={<GridOnIcon />}
                                                            disabled={!attendanceData.length}

                                                            sx={{
                                                                backgroundColor: "#4CAF50",
                                                                "&:hover": { backgroundColor: "#45A049" },
                                                                width: { xs: "100%", sm: "fit-content" },
                                                                marginTop: { xs: 1, sm: 0 }
                                                            }}
                                                        >
                                                            Export to EXCEL
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {value === 'today' && (
                                            <TableContainer component={Paper} sx={{ borderRadius: "0px", width: '100%', overflowX: "auto" }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: "#ECEDFF" }}>
                                                            <TableCell sx={{ fontWeight: "bold", color: "black" }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                                                                    <strong>Name</strong>
                                                                    {sortConfig.key === 'name' && (
                                                                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: "bold", color: "black" }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('date')}>
                                                                    <strong>Date</strong>
                                                                    {sortConfig.key === 'date' && (
                                                                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: "bold", color: "black" }}><strong>Punch In</strong></TableCell>
                                                            <TableCell sx={{ fontWeight: "bold", color: "black" }}><strong>Punch Out</strong></TableCell>
                                                            <TableCell sx={{ fontWeight: "bold", color: "black" }}><strong>Delay Time</strong></TableCell>
                                                            <TableCell sx={{ fontWeight: "bold", color: "black" }}><strong>Working Hours</strong></TableCell>
                                                            <TableCell sx={{ fontWeight: "bold", color: "black" }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('attendance_status')}>
                                                                    <strong>Status</strong>
                                                                    {sortConfig.key === 'attendance_status' && (
                                                                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {hasSubmitted ? (
                                                            getSortedData(getFilteredData(getPaginatedData())).length > 0 ? (
                                                                getSortedData(getFilteredData(getPaginatedData())).map((item) => (
                                                                    <TableRow key={item.userid}>
                                                                        <TableCell>
                                                                            {item.name}
                                                                            <br />
                                                                            <Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>
                                                                                {item.location ? getLocation(item.location) : '-'} | {item.userid}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>{formatDate(item.date)}</TableCell>
                                                                        <TableCell>{trimSeconds(item.intime)}</TableCell>
                                                                        <TableCell>{trimSeconds(item.outtime)}</TableCell>
                                                                        <TableCell>{formatTimeToHoursMinutes(item.delay)}</TableCell>
                                                                        <TableCell>{formatTimeToHoursMinutes(item.workinghour)}</TableCell>
                                                                        <TableCell>{getStatusBadge(item.attendance_status)}</TableCell>
                                                                    </TableRow>
                                                                ))
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colSpan={7} align="center">
                                                                        No attendance data available
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell colSpan={7} align="center">
                                                                    Click Submit to see attendance data
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                        {value === 'historical' && (
                                            <TableContainer component={Paper} sx={{ borderRadius: "0px", width: '100%' }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: "#ECEDFF" }}>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                                                                    <strong>Name</strong>
                                                                    {sortConfig.key === 'name' && (
                                                                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            {attendanceData && generateDateHeaders(startDate, endDate).map((date) => (
                                                                <TableCell key={date}>
                                                                    <strong>{formatDate(date, true)}</strong>
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {hasSubmitted ? (
                                                            getSortedData(getFilteredData(getPaginatedData())).length > 0 ? (
                                                                getSortedData(getFilteredData(getPaginatedData())).map((item) => (
                                                                    <TableRow key={item.userid}>
                                                                        <TableCell>
                                                                            {item.name}
                                                                            <br />
                                                                            <Typography variant="body2" sx={{ fontSize: '12px', color: 'gray' }}>
                                                                                {item.location ? getLocation(item.location) : '-'} | {item.userid}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        {generateDateHeaders(startDate, endDate).map((date) => (
                                                                            <TableCell key={date}>
                                                                                {getStatusBadge(item.month[formatMonth(date)]) || '-'}
                                                                            </TableCell>
                                                                        ))}
                                                                    </TableRow>
                                                                ))
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colSpan={7} align="center">
                                                                        No attendance data available
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell colSpan={7} align="center">
                                                                    Click Submit to see attendance data
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                        <TablePagination
                                            component="div"
                                            count={dashboardData.total}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            rowsPerPageOptions={[10, 25, 50]}
                                            sx={{
                                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                                    margin: '0',
                                                },
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default BranchAdminAttendance;
